import { useMemo, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { services, WHATSAPP_NUMBER } from "../content/siteContent";
import { Reveal, Overline } from "./Reveal";
import { IconWhatsApp, IconCheck } from "./Icons";

const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const hourLabel = (h, isRtl) => {
    const h12 = ((h + 11) % 12) + 1;
    const suffix = h < 12 ? (isRtl ? "صباحاً" : "AM") : isRtl ? "مساءً" : "PM";
    return `${h12}:00 ${suffix}`;
};

const inputClass =
    "w-full min-h-[48px] rounded-xl border border-ink/15 bg-surface px-4 py-3 text-base text-ink placeholder:text-mist/50 transition-[border-color,box-shadow] duration-300 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20";

const Field = ({ id, label, error, children }) => (
    <div>
        <label htmlFor={id} className="mb-2 block text-sm font-semibold text-ink">
            {label}
        </label>
        {children}
        {error && (
            <p id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="mt-2 text-sm font-medium text-red-700 dark:text-red-400">
                {error}
            </p>
        )}
    </div>
);

const AppointmentForm = () => {
    const { t, lang, isRtl } = useLanguage();
    const [values, setValues] = useState({ name: "", phone: "", service: "", section: "", date: "", time: "", notes: "" });
    const [errors, setErrors] = useState({});
    const [wa, setWa] = useState(null); // { state: 'ready' | 'blocked', url }

    const minDate = useMemo(todayISO, []);
    const set = (k) => (e) => {
        const v = e && e.target ? e.target.value : e;
        setValues((prev) => ({ ...prev, [k]: v }));
        setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
    };

    const slots = useMemo(() => {
        const start = values.section === "men" ? 10 : 9;
        const end = values.section === "women" ? 17 : 19;
        const out = [];
        for (let h = start; h <= end; h++) out.push(h);
        return out;
    }, [values.section]);

    const validate = () => {
        const errs = {};
        if (!values.name.trim() || values.name.trim().length < 2) errs.name = t.form.errors.name;
        if (!/^[+\d][\d\s-]{6,}$/.test(values.phone.trim())) errs.phone = t.form.errors.phone;
        if (!values.service) errs.service = t.form.errors.service;
        if (!values.section) errs.section = t.form.errors.section;
        if (!values.date) errs.date = t.form.errors.date;
        else if (values.date < minDate) errs.date = t.form.errors.datePast;
        if (!values.time) errs.time = t.form.errors.time;
        return errs;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        const first = Object.keys(errs)[0];
        if (first) {
            document.getElementById(`appt-${first}`)?.focus();
            setWa(null);
            return;
        }
        const serviceName = services.find((s) => s.id === values.service)?.[lang].name || values.service;
        const sectionName = values.section === "women" ? t.form.wa.women : t.form.wa.men;
        const timeName = hourLabel(Number(values.time), isRtl);
        const lines = [
            t.form.wa.title,
            "——————————",
            `${t.form.wa.name}: ${values.name.trim()}`,
            `${t.form.wa.phone}: ${values.phone.trim()}`,
            `${t.form.wa.service}: ${serviceName}`,
            `${t.form.wa.section}: ${sectionName}`,
            `${t.form.wa.date}: ${values.date}`,
            `${t.form.wa.time}: ${timeName}`,
        ];
        if (values.notes.trim()) lines.push(`${t.form.wa.notes}: ${values.notes.trim()}`);
        lines.push("——————————", t.form.wa.footer);
        const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
        const win = window.open(url, "_blank");
        if (win) {
            win.opener = null;
            setWa({ state: "ready", url });
        } else {
            setWa({ state: "blocked", url });
        }
    };

    return (
        <section id="appointment" className="scroll-mt-24 bg-tint/60 py-12 md:py-20" aria-labelledby="appointment-title" data-testid="appointment-section">
            <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:gap-16 lg:px-8">
                <Reveal className="lg:col-span-2">
                    <Overline>{t.form.overline}</Overline>
                    <h2 id="appointment-title" className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        {t.form.title}
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-mist md:text-lg">{t.form.subtitle}</p>
                    <div className="mt-8 rounded-2xl border border-gold-500/40 bg-gold-100/50 p-6">
                        <p className="flex gap-3 text-sm font-medium leading-relaxed text-ink">
                            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold-500 text-ink">
                                <IconCheck className="h-3.5 w-3.5" />
                            </span>
                            {t.form.disclaimer}
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.1} className="lg:col-span-3">
                    <form
                        onSubmit={onSubmit}
                        noValidate
                        data-testid="appointment-form"
                        className="rounded-3xl border border-ink/10 bg-surface p-6 shadow-[0_30px_60px_-35px_rgba(0,77,77,0.35)] md:p-10"
                    >
                        <div className="grid gap-5 sm:grid-cols-2">
                            <Field id="appt-name" label={t.form.name} error={errors.name}>
                                <input
                                    id="appt-name"
                                    type="text"
                                    autoComplete="name"
                                    value={values.name}
                                    onChange={set("name")}
                                    placeholder={t.form.namePh}
                                    aria-invalid={!!errors.name}
                                    aria-describedby={errors.name ? "appt-name-error" : undefined}
                                    data-testid="appt-name-input"
                                    className={inputClass}
                                />
                            </Field>
                            <Field id="appt-phone" label={t.form.phone} error={errors.phone}>
                                <input
                                    id="appt-phone"
                                    type="tel"
                                    dir="ltr"
                                    autoComplete="tel"
                                    value={values.phone}
                                    onChange={set("phone")}
                                    placeholder={t.form.phonePh}
                                    aria-invalid={!!errors.phone}
                                    aria-describedby={errors.phone ? "appt-phone-error" : undefined}
                                    data-testid="appt-phone-input"
                                    className={`${inputClass} text-start`}
                                />
                            </Field>
                            <Field id="appt-service" label={t.form.service} error={errors.service}>
                                <select
                                    id="appt-service"
                                    value={values.service}
                                    onChange={set("service")}
                                    aria-invalid={!!errors.service}
                                    aria-describedby={errors.service ? "appt-service-error" : undefined}
                                    data-testid="appt-service-select"
                                    className={`${inputClass} ${values.service ? "" : "text-mist/60"}`}
                                >
                                    <option value="" disabled>
                                        {t.form.servicePh}
                                    </option>
                                    {services.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s[lang].name}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <fieldset>
                                <legend className="mb-2 text-sm font-semibold text-ink">{t.form.section}</legend>
                                <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={t.form.section}>
                                    {[
                                        { v: "women", label: t.form.sectionWomen },
                                        { v: "men", label: t.form.sectionMen },
                                    ].map((opt) => (
                                        <label
                                            key={opt.v}
                                            className={`flex min-h-[48px] cursor-pointer items-center justify-center rounded-xl border px-3 text-sm font-semibold transition-[background-color,border-color,color] duration-300 ${
                                                values.section === opt.v
                                                    ? "border-pine bg-pine text-cream"
                                                    : "border-ink/15 bg-surface text-ink hover:border-gold-500"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="appt-section"
                                                value={opt.v}
                                                checked={values.section === opt.v}
                                                onChange={set("section")}
                                                className="sr-only"
                                                data-testid={`appt-section-${opt.v}`}
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                                {errors.section && (
                                    <p role="alert" data-testid="appt-section-error" className="mt-2 text-sm font-medium text-red-700 dark:text-red-400">
                                        {errors.section}
                                    </p>
                                )}
                            </fieldset>
                            <Field id="appt-date" label={t.form.date} error={errors.date}>
                                <input
                                    id="appt-date"
                                    type="date"
                                    min={minDate}
                                    value={values.date}
                                    onChange={set("date")}
                                    aria-invalid={!!errors.date}
                                    aria-describedby={errors.date ? "appt-date-error" : undefined}
                                    data-testid="appt-date-input"
                                    className={inputClass}
                                />
                            </Field>
                            <Field id="appt-time" label={t.form.time} error={errors.time}>
                                <select
                                    id="appt-time"
                                    value={values.time}
                                    onChange={set("time")}
                                    aria-invalid={!!errors.time}
                                    aria-describedby={errors.time ? "appt-time-error" : undefined}
                                    data-testid="appt-time-select"
                                    className={`${inputClass} ${values.time ? "" : "text-mist/60"}`}
                                >
                                    <option value="" disabled>
                                        {t.form.timePh}
                                    </option>
                                    {slots.map((h) => (
                                        <option key={h} value={h}>
                                            {hourLabel(h, isRtl)}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <div className="sm:col-span-2">
                                <Field id="appt-notes" label={t.form.notes} error={errors.notes}>
                                    <textarea
                                        id="appt-notes"
                                        rows={3}
                                        value={values.notes}
                                        onChange={set("notes")}
                                        placeholder={t.form.notesPh}
                                        data-testid="appt-notes-input"
                                        className={`${inputClass} min-h-[96px] resize-y`}
                                    />
                                </Field>
                            </div>
                        </div>
                        <button
                            type="submit"
                            data-testid="appt-submit-button"
                            className="mt-7 inline-flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-full bg-gold-500 px-8 text-base font-semibold text-ink shadow-[0_14px_30px_-12px_rgba(178,143,55,0.55)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-gold-600 active:scale-95 sm:w-auto"
                        >
                            <IconWhatsApp className="h-5 w-5" />
                            {t.form.submit}
                        </button>
                        <p className="mt-4 text-xs leading-relaxed text-mist">{t.form.disclaimer}</p>
                        {wa && (
                            <div
                                role="status"
                                data-testid="appt-wa-status"
                                className={`animate-rise mt-5 rounded-xl border p-4 text-sm font-medium leading-relaxed ${
                                    wa.state === "ready" ? "border-pine/30 bg-pine/5 text-pine" : "border-gold-600/50 bg-gold-100/60 text-ink"
                                }`}
                            >
                                {wa.state === "ready" ? (
                                    t.form.waReady
                                ) : (
                                    <>
                                        {t.form.waError}{" "}
                                        <a
                                            href={wa.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="appt-wa-fallback-link"
                                            className="font-semibold text-pine underline underline-offset-4"
                                        >
                                            {t.form.waErrorAction}
                                        </a>
                                    </>
                                )}
                            </div>
                        )}
                    </form>
                </Reveal>
            </div>
        </section>
    );
};

export default AppointmentForm;
